import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { useAppText } from "@/contexts/AppTextContext";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;

// Strip HTML tags and decode common entities to plain text
function stripHtml(html) {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Render plain-text submission: strip HTML if any, preserve line breaks, linkify http/https URLs
function SubmissionText({ text, className = "" }) {
  if (!text) return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  const clean = isHtml ? stripHtml(text) : text;
  const urlPattern = /https?:\/\/[^\s<>"]+/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = urlPattern.exec(clean)) !== null) {
    if (match.index > last) parts.push(clean.slice(last, match.index));
    parts.push({ url: match[0] });
    last = match.index + match[0].length;
  }
  if (last < clean.length) parts.push(clean.slice(last));

  return (
    <p className={`whitespace-pre-wrap break-words leading-relaxed ${className}`}>
      {parts.map((p, i) =>
        typeof p === "string" ? p :
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
          className="text-[#FF8C00] underline break-all hover:text-[#E67A00]">
          {p.url}
        </a>
      )}
    </p>
  );
}
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, Trash2, Pencil, BookOpen, Ticket, FileCheck, Users, ListChecks, X, Palette, Type, MessageCircle, AlertCircle, Send, ShieldCheck, GraduationCap, UserCog, Search, BarChart2 } from "lucide-react";
import StudentPerformance from "@/components/StudentPerformance";
import AnalyticsTab from "@/components/AnalyticsTab";

const ALL_TABS = [
  { id: "analytics",   label: "Analitik",        icon: BarChart2,  roles: ["admin", "coach", "advisor", "mentor"] },
  { id: "courses",     label: "Kursus",           icon: BookOpen,   roles: ["admin", "coach"] },
  { id: "lessons",     label: "Materi",           icon: ListChecks, roles: ["admin", "coach"] },
  { id: "users",       label: "Pengguna",         icon: Users,      roles: ["admin", "coach", "advisor", "mentor"] },
  { id: "submissions", label: "Tugas Siswa",      icon: FileCheck,  roles: ["admin", "coach", "advisor", "mentor"] },
  { id: "landing",     label: "Edit Landing",     icon: Palette,    roles: ["admin"] },
  { id: "app_text",    label: "Edit Konten",      icon: Type,       roles: ["admin"] },
  { id: "vouchers",    label: "Voucher",          icon: Ticket,     roles: ["admin"] },
];

const ROLE_META = {
  admin:    { label: "Admin",    color: "bg-[#6B21A8] text-white",   Icon: ShieldCheck },
  coach:    { label: "Coach",    color: "bg-[#FF8C00] text-white",   Icon: GraduationCap },
  advisor:  { label: "Advisor",  color: "bg-[#0EA5E9] text-white",   Icon: UserCog },
  mentor:   { label: "Mentor",   color: "bg-[#10B981] text-white",   Icon: UserCog },
  student:  { label: "Siswa",    color: "bg-[#F4F2F8] text-[#5C5868]", Icon: Users },
};

export function RoleBadge({ role, size = "sm" }) {
  const meta = ROLE_META[role] || ROLE_META.student;
  const I = meta.Icon;
  const cls = size === "lg" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5";
  return (
    <span className={`${meta.color} ${cls} rounded-full font-bold inline-flex items-center gap-1`} data-testid={`role-badge-${role}`}>
      <I className="w-3 h-3" /> {meta.label.toUpperCase()}
    </span>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const tabs = ALL_TABS.filter((t) => t.roles.includes(user?.role));
  const [tab, setTab] = useState(tabs[0]?.id || "users");
  const [stats, setStats] = useState({});
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [tab]);

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });

  // Stat cards filtered by role
  const isAdminOrCoach = ["admin", "coach"].includes(user?.role);
  const statCards = [
    { k: "users", l: user?.role === "mentor" ? "Siswa saya" : "Pengguna", i: Users, t: "users" },
    isAdminOrCoach && { k: "courses", l: "Kursus", i: BookOpen, t: "courses" },
    { k: "submissions", l: "Tugas Masuk", i: FileCheck, t: "submissions" },
  ].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-panel">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422]">
          {user?.name?.split(" ")[0]}
        </h1>
        <RoleBadge role={user?.role} size="lg" />
      </div>
      <p className="text-[#5C5868] mb-6">
        {user?.role === "admin"
          ? "Kelola seluruh konten, pengguna, dan pantau perkembangan program."
          : user?.role === "coach"
          ? "Kelola kursus & materi, serta pantau perkembangan seluruh siswa."
          : "Pantau tugas dan perkembangan belajar siswa yang diassign ke kamu."}
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {statCards.map(({ k, l, i: I, t: targetTab }) => (
          <button
            key={k}
            onClick={() => tabs.find((tt) => tt.id === targetTab) && setTab(targetTab)}
            className="card-surface p-4 flex items-center gap-3 card-hover text-left"
            data-testid={`stat-card-${k}`}
          >
            <I className="w-5 h-5 text-[#FF8C00]" />
            <div>
              <div className="text-xl font-heading font-extrabold text-[#1A1422]">{stats[k] ?? 0}</div>
              <div className="text-xs text-[#5C5868]">{l}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === t.id ? "bg-[#FF8C00] text-black" : "bg-[#FAF9FC] text-[#5C5868] hover:text-[#1A1422]"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "courses" && <CoursesTab askConfirm={askConfirm} />}
      {tab === "lessons" && <LessonsTab askConfirm={askConfirm} />}
      {tab === "vouchers" && <VouchersTab askConfirm={askConfirm} />}
      {tab === "submissions" && <SubmissionsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "landing" && <LandingTab />}
      {tab === "app_text" && <AppTextTab />}
      {tab === "analytics" && <AnalyticsTab />}

      {confirmState && (
        <ConfirmDialog
          message={confirmState.message}
          onConfirm={async () => {
            await confirmState.onConfirm();
            setConfirmState(null);
          }}
          onCancel={() => setConfirmState(null)}
        />
      )}

    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await onConfirm(); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="confirm-dialog">
      <div className="card-surface max-w-sm w-full p-6">
        <h3 className="font-heading text-lg font-bold text-[#1A1422] mb-2">Konfirmasi</h3>
        <p className="text-[#5C5868] mb-5 text-sm">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={loading} className="btn-ghost" data-testid="confirm-cancel">Batal</button>
          <button onClick={handle} disabled={loading} className="bg-[#EF4444] hover:bg-[#DC2626] text-[#1A1422] font-semibold rounded-full px-5 py-2 transition-colors" data-testid="confirm-ok">
            {loading ? "Memproses..." : "Ya, Hapus"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- COURSES ---------------- */
function CoursesTab({ askConfirm }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // course object or "new"
  const load = async () => setItems((await api.get("/courses")).data);
  useEffect(() => { load(); }, []);

  const remove = (id) => {
    askConfirm("Hapus kursus dan semua materinya?", async () => {
      try {
        await api.delete(`/admin/courses/${id}`);
        toast.success("Kursus dihapus");
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Gagal menghapus");
      }
    });
  };

  return (
    <div>
      <button onClick={() => setEditing({})} className="btn-primary mb-4" data-testid="admin-new-course-button">
        <Plus className="w-4 h-4" /> Tambah Kursus
      </button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.course_id} className="card-surface overflow-hidden" data-testid={`admin-course-${c.course_id}`}>
            <div className="h-32 bg-[#F4F2F8]">{c.thumbnail && <img src={toAbsUrl(c.thumbnail)} className="w-full h-full object-cover" alt="" />}</div>
            <div className="p-4">
              <div className="font-heading font-bold text-[#1A1422] mb-1 line-clamp-1">{c.title}</div>
              <div className="text-xs text-[#5C5868] mb-1">{formatIDR(c.price)} • {c.lesson_count} materi</div>
              {c.enrollment_count != null && (
                <div className="flex items-center gap-1 text-xs text-[#6B21A8] font-semibold mb-3">
                  <Users className="w-3 h-3" /> {c.enrollment_count} peserta terdaftar
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setEditing(c)} className="btn-ghost text-xs"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(c.course_id)} className="btn-ghost text-xs text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /> Hapus</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <CourseModal initial={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CourseModal({ initial, onClose }) {
  const isNew = !initial.course_id;
  const [form, setForm] = useState({
    title: initial.title || "",
    description: initial.description || "",
    thumbnail: initial.thumbnail || "",
    price: initial.price || 0,
    currency: initial.currency || "idr",
    category: initial.category || "Umum",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/courses", form);
      else await api.put(`/admin/courses/${initial.course_id}`, form);
      toast.success("Tersimpan");
      onClose();
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title={isNew ? "Kursus Baru" : "Edit Kursus"}>
      <div className="space-y-3">
        <Field label="Judul"><input className="input-field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-form-title" /></Field>
        <Field label="Deskripsi"><textarea rows={4} className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-form-desc" /></Field>
        <ImageUpload
          value={form.thumbnail}
          onChange={(url) => setForm({ ...form, thumbnail: url })}
          label="Thumbnail Kursus"
          testId="course-thumbnail-upload"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kategori"><input className="input-field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
          <Field label="Harga (IDR)"><input type="number" className="input-field" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="course-form-price" /></Field>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center" data-testid="course-form-save">
          {saving ? "Menyimpan..." : "Simpan"}
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- LESSONS ---------------- */
function LessonsTab({ askConfirm }) {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState("");
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null); // null = tutup, {} = baru, lesson obj = edit
  const [collapsed, setCollapsed] = useState({});
  const [renamingSection, setRenamingSection] = useState(null);

  useEffect(() => {
    api.get("/courses").then(({ data }) => {
      setCourses(data);
      if (data[0]) setSelected(data[0].course_id);
    });
  }, []);

  const loadLessons = async (id) => {
    if (!id) return;
    const { data } = await api.get(`/courses/${id}/lessons`);
    setLessons(data);
  };

  useEffect(() => { if (selected) { loadLessons(selected); setEditing(null); } }, [selected]);

  const remove = (lessonId) => {
    askConfirm("Hapus materi ini?", async () => {
      try {
        await api.delete(`/admin/lessons/${lessonId}`);
        toast.success("Materi dihapus");
        if (editing?.lesson_id === lessonId) setEditing(null);
        loadLessons(selected);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Gagal menghapus");
      }
    });
  };

  const toggleCollapse = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  const saveRenameSection = async () => {
    if (!renamingSection || !renamingSection.newName.trim()) return;
    const toUpdate = lessons.filter(l => l.section === renamingSection.old);
    try {
      await Promise.all(toUpdate.map(l =>
        api.put(`/admin/lessons/${l.lesson_id}`, { ...l, section: renamingSection.newName.trim() })
      ));
      toast.success("Nama modul diperbarui");
      setRenamingSection(null);
      loadLessons(selected);
    } catch {
      toast.error("Gagal rename modul");
    }
  };

  const sectionNames = [...new Set(lessons.filter(l => l.section).map(l => l.section))];
  const standalone = lessons.filter(l => !l.section);
  const sections = sectionNames.map(name => ({
    name,
    items: lessons.filter(l => l.section === name),
    minOrder: Math.min(...lessons.filter(l => l.section === name).map(l => l.order)),
  }));
  const groups = [
    ...standalone.map(l => ({ type: "lesson", lesson: l, order: l.order })),
    ...sections.map(s => ({ type: "section", ...s, order: s.minOrder })),
  ].sort((a, b) => a.order - b.order);

  const LessonRow = ({ l, indent = false }) => {
    const isActive = editing?.lesson_id === l.lesson_id;
    return (
      <div
        className={`p-3 flex items-center justify-between rounded-xl cursor-pointer transition-colors ${isActive ? "bg-[#FF8C00]/10 border border-[#FF8C00]/30" : "hover:bg-[#F4F2F8]"} ${indent ? "ml-4 border-l-2 border-[#FF8C00]/20" : ""}`}
        data-testid={`admin-lesson-${l.lesson_id}`}
        onClick={() => setEditing(isActive ? null : l)}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-[#8B8794]">#{l.order} • {l.video_url ? "🎥" : "📝"}{l.has_assignment ? " • +Tugas" : ""}</div>
          <div className={`text-sm font-semibold truncate ${isActive ? "text-[#FF8C00]" : "text-[#1A1422]"}`}>{l.title}</div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); remove(l.lesson_id); }} className="ml-2 p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444] flex-shrink-0">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex gap-0 min-h-[600px]" style={{ alignItems: "stretch" }}>
      {/* Kolom kiri — daftar materi */}
      <div className="w-72 flex-shrink-0 flex flex-col border-r border-[#E8E5EE]">
        <div className="p-3 border-b border-[#E8E5EE] space-y-2">
          <select className="input-field text-sm" value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="lessons-course-select">
            <option value="">— pilih kursus —</option>
            {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
          </select>
          {selected && (
            <button onClick={() => setEditing({})} className="btn-primary w-full justify-center text-sm py-2" data-testid="admin-new-lesson-button">
              <Plus className="w-4 h-4" /> Tambah Materi
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {groups.map((g) => g.type === "lesson"
            ? <LessonRow key={g.lesson.lesson_id} l={g.lesson} />
            : (
              <div key={g.name} className="mb-1">
                {renamingSection?.old === g.name ? (
                  <div className="flex items-center gap-1 p-1">
                    <input autoFocus className="input-field flex-1 text-xs py-1" value={renamingSection.newName}
                      onChange={e => setRenamingSection(r => ({ ...r, newName: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") saveRenameSection(); if (e.key === "Escape") setRenamingSection(null); }} />
                    <button onClick={saveRenameSection} className="text-xs bg-[#FF8C00] text-white px-2 py-1 rounded-lg">OK</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <button onClick={() => toggleCollapse(g.name)} className="flex items-center gap-1.5 flex-1 text-left min-w-0">
                      <span className="text-[10px] font-bold bg-[#FF8C00]/10 text-[#FF8C00] px-1.5 py-0.5 rounded-full flex-shrink-0">MODUL</span>
                      <span className="text-xs font-semibold text-[#1A1422] truncate">{g.name}</span>
                      <span className="text-[#8B8794] text-xs flex-shrink-0">{collapsed[g.name] ? "▶" : "▼"}</span>
                    </button>
                    <button onClick={() => setRenamingSection({ old: g.name, newName: g.name })} className="p-1 hover:bg-[#F4F2F8] rounded flex-shrink-0">
                      <Pencil className="w-3 h-3 text-[#8B8794]" />
                    </button>
                  </div>
                )}
                {!collapsed[g.name] && renamingSection?.old !== g.name && (
                  <div className="space-y-0.5">
                    {g.items.map(l => <LessonRow key={l.lesson_id} l={l} indent />)}
                  </div>
                )}
              </div>
            )
          )}
          {lessons.length === 0 && selected && (
            <p className="text-xs text-[#8B8794] text-center py-6">Belum ada materi</p>
          )}
        </div>
      </div>

      {/* Kolom kanan — form editor */}
      <div className="flex-1 min-w-0">
        {editing !== null ? (
          <LessonForm
            courseId={selected}
            lessons={lessons}
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { loadLessons(selected); setEditing(null); }}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-8 text-[#8B8794]">
            <ListChecks className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Pilih materi di kiri untuk mengedit,<br />atau klik <strong>Tambah Materi</strong> untuk membuat baru.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function LessonForm({ courseId, lessons = [], initial, onClose, onSaved }) {
  const isNew = !initial.lesson_id;
  const existingSections = [...new Set(lessons.filter(l => l.section).map(l => l.section))];
  const [form, setForm] = useState({
    title: initial.title || "",
    content_type: initial.content_type || "text",
    content: initial.content || "",
    video_url: initial.video_url || "",
    order: initial.order ?? 1,
    has_assignment: initial.has_assignment || false,
    assignment_title: initial.assignment_title || "",
    assignment_description: initial.assignment_description || "",
    section: initial.section || "",
  });
  const [sectionMode, setSectionMode] = useState(
    initial.section ? (existingSections.includes(initial.section) ? "pick" : "new") : "none"
  );
  const [saving, setSaving] = useState(false);

  // Reset form when initial changes (different lesson selected)
  useEffect(() => {
    setForm({
      title: initial.title || "",
      content_type: initial.content_type || "text",
      content: initial.content || "",
      video_url: initial.video_url || "",
      order: initial.order ?? 1,
      has_assignment: initial.has_assignment || false,
      assignment_title: initial.assignment_title || "",
      assignment_description: initial.assignment_description || "",
      section: initial.section || "",
    });
    setSectionMode(initial.section ? (existingSections.includes(initial.section) ? "pick" : "new") : "none");
  }, [initial.lesson_id]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post(`/admin/courses/${courseId}/lessons`, form);
      else await api.put(`/admin/lessons/${initial.lesson_id}`, form);
      toast.success("Tersimpan");
      onSaved();
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="lesson-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-[#E8E5EE] flex-shrink-0">
        <h2 className="font-heading font-bold text-[#1A1422] text-sm">{isNew ? "Tambah Materi Baru" : `Edit: ${initial.title || ""}`}</h2>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="btn-ghost text-xs px-3 py-1.5">Batal</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm py-2 px-4" data-testid="lesson-form-save">
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      {/* Body — kiri settings, kanan editor */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 grid lg:grid-cols-[320px_1fr] gap-4 min-w-0">

          {/* Kolom kiri — pengaturan */}
          <div className="space-y-3">
            <div className="card-surface p-4 space-y-3">
              <div className="text-xs font-semibold text-[#5C5868] uppercase tracking-wider">Info Materi</div>
              <Field label="Judul Materi">
                <input className="input-field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-form-title" placeholder="Judul materi..." />
              </Field>
              <Field label="Urutan">
                <input type="number" className="input-field" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 1 })} />
              </Field>
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-xs font-semibold text-[#5C5868] uppercase tracking-wider">Bagian dari Modul</div>
              <div className="flex flex-wrap gap-2">
                {[["none","Mandiri"],["pick","Modul Ada"],["new","Modul Baru"]].map(([v,l]) => (
                  <button key={v} type="button"
                    onClick={() => { setSectionMode(v); setForm(f => ({ ...f, section: v === "none" ? "" : f.section })); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sectionMode === v ? "bg-[#FF8C00] text-white border-[#FF8C00]" : "bg-white text-[#5C5868] border-[#E8E5EE] hover:border-[#FF8C00]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {sectionMode === "pick" && existingSections.length > 0 && (
                <select className="input-field" value={form.section} onChange={(e) => setForm(f => ({ ...f, section: e.target.value }))}>
                  <option value="">— pilih modul —</option>
                  {existingSections.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {sectionMode === "pick" && existingSections.length === 0 && (
                <p className="text-xs text-[#8B8794]">Belum ada modul. Pilih "Modul Baru".</p>
              )}
              {sectionMode === "new" && (
                <input className="input-field" placeholder="Nama modul..." value={form.section} onChange={(e) => setForm(f => ({ ...f, section: e.target.value }))} />
              )}
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-xs font-semibold text-[#5C5868] uppercase tracking-wider">🎥 Link Video (opsional)</div>
              <input className="input-field" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid="lesson-form-video" />
              <p className="text-xs text-[#8B8794]">Tempel link YouTube. Kosongkan kalau hanya teks.</p>
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-xs font-semibold text-[#5C5868] uppercase tracking-wider">📝 Tugas</div>
              <label className="flex items-center gap-2 text-sm text-[#1A1422] cursor-pointer">
                <input type="checkbox" checked={form.has_assignment} onChange={(e) => setForm({ ...form, has_assignment: e.target.checked })} data-testid="lesson-form-has-assignment" />
                Materi ini punya tugas
              </label>
              {form.has_assignment && (
                <div className="space-y-2">
                  <Field label="Judul tugas"><input className="input-field" value={form.assignment_title} onChange={(e) => setForm({ ...form, assignment_title: e.target.value })} /></Field>
                  <Field label="Instruksi tugas"><textarea rows={4} className="input-field" value={form.assignment_description} onChange={(e) => setForm({ ...form, assignment_description: e.target.value })} /></Field>
                </div>
              )}
            </div>
          </div>

          {/* Kolom kanan — editor teks */}
          <div className="card-surface p-4 flex flex-col min-w-0" style={{ minHeight: 480 }}>
            <div className="text-xs font-semibold text-[#5C5868] uppercase tracking-wider mb-3">📄 Konten / Penjelasan Teks</div>
            <div className="flex-1">
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm({ ...form, content: html })}
                placeholder="Tulis penjelasan materi di sini..."
                testId="lesson-form-content"
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ---------------- VOUCHERS ---------------- */
function VouchersTab({ askConfirm }) {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ code: "", course_id: "", discount_percent: 100, max_uses: 100 });
  const [accessCode, setAccessCode] = useState("");
  const [newAccessCode, setNewAccessCode] = useState("");
  const [savingCode, setSavingCode] = useState(false);

  const load = async () => {
    setItems((await api.get("/admin/vouchers")).data);
    setCourses((await api.get("/courses")).data);
    try { const r = await api.get("/admin/access-code"); setAccessCode(r.data.code); } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveAccessCode = async () => {
    if (newAccessCode.trim().length < 4) return toast.error("Minimal 4 karakter");
    setSavingCode(true);
    try {
      await api.put("/admin/access-code", { code: newAccessCode.trim().toUpperCase() });
      setAccessCode(newAccessCode.trim().toUpperCase());
      setNewAccessCode("");
      toast.success("Kode akses berhasil diperbarui");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setSavingCode(false); }
  };

  const create = async () => {
    if (!form.code) return toast.error("Kode wajib diisi");
    try {
      await api.post("/admin/vouchers", { ...form, course_id: form.course_id || null });
      toast.success("Voucher dibuat");
      setForm({ code: "", course_id: "", discount_percent: 100, max_uses: 100 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = (id, code) => {
    askConfirm(`Hapus voucher "${code}"? Tindakan ini tidak bisa dibatalkan.`, async () => {
      try {
        await api.delete(`/admin/vouchers/${id}`);
        toast.success("Voucher dihapus");
        load();
      } catch (e) {
        console.error("Delete voucher failed:", e);
        toast.error(e.response?.data?.detail || "Gagal menghapus voucher");
      }
    });
  };

  return (
    <div>
      {/* Kode Akses */}
      <div className="card-surface p-5 mb-6">
        <h3 className="font-heading font-bold text-[#1A1422] mb-1">Kode Akses Siswa</h3>
        <p className="text-xs text-[#8B8794] mb-4">Siswa baru wajib memasukkan kode ini sebelum bisa mengakses dashboard dan kursus.</p>
        {accessCode && (
          <div className="flex items-center justify-between bg-[#F4F2F8] rounded-xl px-4 py-3 mb-4">
            <div>
              <div className="text-[10px] text-[#8B8794] mb-1">Kode aktif saat ini</div>
              <div className="font-mono text-[#6B21A8] font-bold text-lg tracking-widest">{accessCode}</div>
            </div>
            <span className="text-xs bg-[#EAF3DE] text-[#3B6D11] font-semibold px-3 py-1 rounded-full">Aktif</span>
          </div>
        )}
        {!accessCode && (
          <div className="bg-[#FFF3E0] text-[#854F0B] text-xs rounded-xl px-4 py-3 mb-4">
            ⚠ Kode akses belum diatur. Siswa tidak bisa masuk sampai kode diset.
          </div>
        )}
        <div className="flex gap-3">
          <input
            className="input-field flex-1 uppercase"
            placeholder="Kode baru (min. 4 karakter)"
            value={newAccessCode}
            onChange={e => setNewAccessCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && saveAccessCode()}
          />
          <button onClick={saveAccessCode} disabled={newAccessCode.trim().length < 4 || savingCode} className="btn-primary">
            {savingCode ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
        <p className="text-xs text-[#8B8794] mt-2">Saat kode diganti, siswa yang sudah verifikasi tetap bisa akses. Hanya pendaftar baru yang perlu kode baru.</p>
      </div>

      <div className="card-surface p-5 mb-6">
        <h3 className="font-heading font-bold text-[#1A1422] mb-4">Buat Voucher Baru</h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <input className="input-field uppercase" placeholder="KODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="voucher-form-code" />
          <select className="input-field" value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })}>
            <option value="">— semua kursus —</option>
            {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
          </select>
          <input type="number" min={1} max={100} className="input-field" placeholder="Diskon %" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: parseInt(e.target.value) || 0 })} data-testid="voucher-form-discount" />
          <input type="number" min={1} className="input-field" placeholder="Max pakai" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 1 })} />
        </div>
        <button onClick={create} className="btn-primary mt-4" data-testid="voucher-form-create">
          <Plus className="w-4 h-4" /> Buat
        </button>
      </div>

      <div className="space-y-2">
        {items.map((v) => (
          <div key={v.voucher_id} className="card-surface p-4 flex items-center justify-between" data-testid={`admin-voucher-${v.code}`}>
            <div>
              <div className="font-mono text-[#FF8C00] font-bold">{v.code}</div>
              <div className="text-xs text-[#5C5868]">Diskon {v.discount_percent}% • {v.used_count}/{v.max_uses} terpakai • {v.course_id ? "Khusus 1 kursus" : "Semua kursus"}</div>
            </div>
            <button onClick={() => remove(v.voucher_id, v.code)} className="btn-ghost text-[#EF4444]" data-testid={`delete-voucher-${v.code}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- SUBMISSIONS ---------------- */
function SubmissionsTab() {
  const { user: me } = useAuth();
  const canArchive = ["admin", "coach"].includes(me?.role);
  const [items, setItems] = useState([]);
  const [grading, setGrading] = useState(null);
  const [filterName, setFilterName] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [searching, setSearching] = useState(false);

  const load = async (name = filterName, from = dateFrom, to = dateTo, archived = showArchived) => {
    setSearching(true);
    try {
      const p = new URLSearchParams();
      if (name.trim()) p.set("student_name", name.trim());
      if (from) p.set("date_from", from);
      if (to) p.set("date_to", to);
      if (archived) p.set("archived", "true");
      const qs = p.toString() ? `?${p.toString()}` : "";
      setItems((await api.get(`/admin/submissions${qs}`)).data);
    } finally { setSearching(false); }
  };

  useEffect(() => { load("", "", "", false); }, []);

  const handleSearch = (e) => {
    const val = e.target.value;
    setFilterName(val);
    clearTimeout(window._subSearchTimer);
    window._subSearchTimer = setTimeout(() => load(val), 400);
  };

  const handleArchiveToggle = async (s) => {
    const endpoint = s.is_archived ? "unarchive" : "archive";
    try {
      await api.post(`/admin/submissions/${s.submission_id}/${endpoint}`);
      toast.success(s.is_archived ? "Tugas dipulihkan" : "Tugas diarsipkan");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Gagal mengarsipkan";
      toast.error(msg);
      console.error("Archive error:", err?.response?.data || err);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Hapus permanen tugas dari ${s.student_name || "siswa ini"}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/admin/submissions/${s.submission_id}`);
      toast.success("Tugas dihapus permanen");
      load();
    } catch (err) {
      toast.error("Gagal menghapus tugas");
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="mb-4 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8794]" />
          <input value={filterName} onChange={handleSearch} placeholder="Cari nama siswa..."
            className="input-field text-sm" style={{ paddingLeft: "2.25rem" }} />
          {searching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B8794]">Mencari...</span>}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#5C5868] whitespace-nowrap">Dari</label>
            <input type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); load(filterName, e.target.value, dateTo); }}
              className="input-field text-sm py-2 px-3" style={{ width: "auto" }} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#5C5868] whitespace-nowrap">Sampai</label>
            <input type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); load(filterName, dateFrom, e.target.value); }}
              className="input-field text-sm py-2 px-3" style={{ width: "auto" }} />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); load(filterName, "", ""); }}
              className="text-xs text-[#EF4444] hover:underline">Reset tanggal</button>
          )}
          <button
            onClick={() => { const next = !showArchived; setShowArchived(next); load(filterName, dateFrom, dateTo, next); }}
            className={`ml-auto text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${showArchived ? "bg-[#6B21A8] text-white" : "bg-[#F4F2F8] text-[#5C5868] hover:bg-[#E8E5EE]"}`}>
            {showArchived ? "Lihat Aktif" : "Lihat Arsip"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="card-surface p-6 text-[#5C5868] text-sm">Belum ada tugas masuk.</div>}
        {items.map((s) => (
          <div key={s.submission_id} className="card-surface p-5" data-testid={`admin-submission-${s.submission_id}`}>
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-[#1A1422]">{s.user_name} <span className="font-normal text-[#5C5868]">— {s.lesson_title}</span></div>
                  {s.is_archived && <span className="text-[10px] bg-[#6B21A8]/10 text-[#6B21A8] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">Arsip</span>}
                </div>
                <div className="text-xs text-[#8B8794] mt-0.5">{new Date(s.submitted_at).toLocaleString("id-ID")}</div>
              </div>
              {s.grade != null ? (
                <span className="text-xs bg-[#10B981]/10 text-[#10B981] px-3 py-1 rounded-full font-semibold">Nilai: {s.grade}/100</span>
              ) : (
                <span className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-3 py-1 rounded-full font-semibold">Belum dinilai</span>
              )}
            </div>

            {/* Jawaban siswa */}
            <div className="bg-[#FAF9FC] border border-[#E8E5EE] rounded-xl p-4 mb-3">
              <p className="text-[10px] font-semibold text-[#8B8794] uppercase tracking-wide mb-2">Jawaban Siswa</p>
              <SubmissionText text={s.content} className="text-sm text-[#1A1422]" />
            </div>

            {/* Feedback yang sudah ada */}
            {s.feedback && (
              <div className="bg-[#FFF4E6] border border-[#FFD8A8] rounded-xl p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  {s.graded_by_picture ? (
                    <img src={toAbsUrl(s.graded_by_picture)} className="w-6 h-6 rounded-full object-cover" alt="" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#FF8C00] flex items-center justify-center text-white text-[10px] font-bold">
                      {s.graded_by_name?.[0] || "?"}
                    </div>
                  )}
                  <span className="text-xs font-semibold text-[#FF8C00]">{s.graded_by_name || "BisaUsaha"}</span>
                  {s.graded_by_role && (
                    <span className="text-[10px] text-[#8B8794] capitalize">· {s.graded_by_role}</span>
                  )}
                  {s.graded_at && (
                    <span className="text-[10px] text-[#8B8794] ml-auto">{new Date(s.graded_at).toLocaleString("id-ID")}</span>
                  )}
                </div>
                <p className="text-sm text-[#1A1422]">{s.feedback}</p>
              </div>
            )}

            {/* Tombol aksi */}
            <div className="flex flex-wrap gap-2 mt-2">
              {!s.is_archived && <>
                <button onClick={() => setGrading({ ...s, mode: "feedback" })} className="btn-outline text-sm" data-testid={`feedback-button-${s.submission_id}`}>
                  {s.feedback ? "Edit Feedback" : "Beri Feedback"}
                </button>
                <button onClick={() => setGrading({ ...s, mode: "grade" })} className="btn-outline text-sm" data-testid={`grade-button-${s.submission_id}`}>
                  {s.grade != null ? "Edit Nilai" : "Beri Nilai"}
                </button>
              </>}
              {canArchive && (
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => handleArchiveToggle(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#E8E5EE] text-[#8B8794] hover:border-[#6B21A8] hover:text-[#6B21A8] transition-colors">
                    {s.is_archived ? "Pulihkan" : "Arsipkan"}
                  </button>
                  {s.is_archived && (
                    <button onClick={() => handleDelete(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors">
                      Hapus Permanen
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {grading && <GradeModal sub={grading} onClose={() => { setGrading(null); load(filterName); }} />}
    </div>
  );
}

function GradeModal({ sub, onClose }) {
  const mode = sub.mode || "grade";
  const [grade, setGrade] = useState(sub.grade ?? 80);
  const [feedback, setFeedback] = useState(sub.feedback ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (mode === "feedback" && !feedback.trim()) { toast.error("Feedback tidak boleh kosong"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/submissions/${sub.submission_id}/grade`, {
        grade: mode === "grade" ? (parseInt(grade) || 0) : (sub.grade ?? null),
        feedback,
      });
      toast.success(mode === "feedback" ? "Feedback disimpan" : "Nilai disimpan");
      onClose();
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title={mode === "feedback" ? "Beri Feedback" : "Beri Nilai"}>
      <div className="space-y-4">
        {/* Jawaban siswa */}
        <div>
          <p className="text-[10px] font-semibold text-[#8B8794] uppercase tracking-wide mb-2">Jawaban {sub.user_name}</p>
          <div className="bg-[#FAF9FC] border border-[#E8E5EE] rounded-xl p-4 max-h-48 overflow-y-auto">
            <SubmissionText text={sub.content} className="text-sm text-[#1A1422]" />
          </div>
        </div>

        {mode === "grade" && (
          <Field label="Nilai (0–100)">
            <input type="number" min={0} max={100} className="input-field" value={grade}
              onChange={e => setGrade(e.target.value)} data-testid="grade-input" />
          </Field>
        )}

        <Field label="Feedback untuk siswa">
          <textarea rows={4} className="input-field" placeholder="Tulis komentar, saran, atau apresiasi untuk siswa..."
            value={feedback} onChange={e => setFeedback(e.target.value)} />
        </Field>

        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center" data-testid="grade-save-button">
          {saving ? "Menyimpan..." : mode === "feedback" ? "Kirim Feedback" : "Simpan Nilai & Feedback"}
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- Shared ---------------- */
function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-bold text-[#1A1422]">{title}</h3>
          <button onClick={onClose} className="text-[#5C5868] hover:text-[#1A1422]"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5C5868] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}


/* ---------------- LANDING PAGE CONTENT ---------------- */
const ICON_OPTIONS = ["BookOpen", "Video", "MessageSquare", "Award", "Users", "Trophy", "Zap", "Target", "Heart", "Star", "Sparkles"];

function LandingTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/landing-config").then(({ data }) => setCfg(data));
  }, []);

  if (!cfg) return <div className="text-[#5C5868]">Memuat...</div>;

  const update = (k, v) => setCfg({ ...cfg, [k]: v });

  const updateFeature = (i, k, v) => {
    const features = [...(cfg.features || [])];
    features[i] = { ...features[i], [k]: v };
    setCfg({ ...cfg, features });
  };

  const addFeature = () => setCfg({ ...cfg, features: [...(cfg.features || []), { icon: "BookOpen", title: "Fitur baru", desc: "Deskripsi singkat" }] });
  const removeFeature = (i) => setCfg({ ...cfg, features: cfg.features.filter((_, idx) => idx !== i) });

  const addBullet = () => setCfg({ ...cfg, hero_bullets: [...(cfg.hero_bullets || []), "Poin baru"] });
  const updateBullet = (i, v) => {
    const arr = [...(cfg.hero_bullets || [])];
    arr[i] = v;
    setCfg({ ...cfg, hero_bullets: arr });
  };
  const removeBullet = (i) => setCfg({ ...cfg, hero_bullets: cfg.hero_bullets.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/landing-config", cfg);
      toast.success("Konten landing tersimpan! Refresh landing page untuk lihat hasilnya.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* HERO */}
      <section className="card-surface p-5">
        <h3 className="font-heading text-lg font-bold text-[#1A1422] mb-4">Hero Section</h3>
        <div className="space-y-3">
          <Field label="Eyebrow (label kecil di atas judul)">
            <input className="input-field" value={cfg.hero_eyebrow} onChange={(e) => update("hero_eyebrow", e.target.value)} data-testid="landing-hero-eyebrow" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Judul - baris 1"><input className="input-field" value={cfg.hero_title_part1} onChange={(e) => update("hero_title_part1", e.target.value)} /></Field>
            <Field label="Judul - baris 2"><input className="input-field" value={cfg.hero_title_part2} onChange={(e) => update("hero_title_part2", e.target.value)} /></Field>
            <Field label="Kata highlight (warna oranye)"><input className="input-field" value={cfg.hero_title_highlight} onChange={(e) => update("hero_title_highlight", e.target.value)} /></Field>
            <Field label="Judul - lanjutan baris 3"><input className="input-field" value={cfg.hero_title_part3} onChange={(e) => update("hero_title_part3", e.target.value)} /></Field>
          </div>
          <Field label="Subjudul / deskripsi">
            <textarea rows={3} className="input-field" value={cfg.hero_subtitle} onChange={(e) => update("hero_subtitle", e.target.value)} data-testid="landing-hero-subtitle" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Tombol utama (CTA)"><input className="input-field" value={cfg.hero_cta_primary} onChange={(e) => update("hero_cta_primary", e.target.value)} /></Field>
            <Field label="Tombol kedua"><input className="input-field" value={cfg.hero_cta_secondary} onChange={(e) => update("hero_cta_secondary", e.target.value)} /></Field>
          </div>
          <ImageUpload
            value={cfg.hero_image}
            onChange={(url) => update("hero_image", url)}
            label="Gambar Hero"
            testId="landing-hero-image-upload"
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Badge nilai (mis. 100%)"><input className="input-field" value={cfg.hero_badge_value} onChange={(e) => update("hero_badge_value", e.target.value)} /></Field>
            <Field label="Badge teks"><input className="input-field" value={cfg.hero_badge_text} onChange={(e) => update("hero_badge_text", e.target.value)} /></Field>
          </div>

          <div>
            <div className="text-xs text-[#5C5868] mb-2">Poin-poin di bawah tombol CTA</div>
            {(cfg.hero_bullets || []).map((b, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className="input-field flex-1" value={b} onChange={(e) => updateBullet(i, e.target.value)} />
                <button onClick={() => removeBullet(i)} className="btn-ghost text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addBullet} className="btn-outline text-sm py-1.5"><Plus className="w-3.5 h-3.5" /> Tambah poin</button>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="card-surface p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-[#1A1422]">Kartu Fitur</h3>
          <button onClick={addFeature} className="btn-outline text-sm py-1.5"><Plus className="w-3.5 h-3.5" /> Tambah</button>
        </div>
        <div className="space-y-4">
          {(cfg.features || []).map((f, i) => (
            <div key={i} className="bg-[#F4F2F8] rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5C5868] font-semibold">Fitur #{i + 1}</span>
                <button onClick={() => removeFeature(i)} className="btn-ghost text-[#EF4444] text-xs"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                <select className="input-field" value={f.icon} onChange={(e) => updateFeature(i, "icon", e.target.value)}>
                  {ICON_OPTIONS.map((io) => <option key={io} value={io}>{io}</option>)}
                </select>
                <input className="input-field sm:col-span-2" value={f.title} placeholder="Judul" onChange={(e) => updateFeature(i, "title", e.target.value)} />
              </div>
              <textarea rows={2} className="input-field" value={f.desc} placeholder="Deskripsi singkat" onChange={(e) => updateFeature(i, "desc", e.target.value)} />
            </div>
          ))}
        </div>
      </section>

      {/* COURSES TITLE */}
      <section className="card-surface p-5">
        <h3 className="font-heading text-lg font-bold text-[#1A1422] mb-4">Section Kursus</h3>
        <div className="space-y-3">
          <Field label="Judul section"><input className="input-field" value={cfg.courses_title} onChange={(e) => update("courses_title", e.target.value)} /></Field>
          <Field label="Subjudul"><input className="input-field" value={cfg.courses_subtitle} onChange={(e) => update("courses_subtitle", e.target.value)} /></Field>
        </div>
      </section>

      {/* CTA BOTTOM */}
      <section className="card-surface p-5">
        <h3 className="font-heading text-lg font-bold text-[#1A1422] mb-4">Section CTA Bawah</h3>
        <div className="space-y-3">
          <Field label="Judul besar"><input className="input-field" value={cfg.cta_title} onChange={(e) => update("cta_title", e.target.value)} /></Field>
          <Field label="Subjudul"><textarea rows={2} className="input-field" value={cfg.cta_subtitle} onChange={(e) => update("cta_subtitle", e.target.value)} /></Field>
          <Field label="Teks tombol"><input className="input-field" value={cfg.cta_button} onChange={(e) => update("cta_button", e.target.value)} /></Field>
        </div>
      </section>

      {/* FOOTER */}
      <section className="card-surface p-5">
        <h3 className="font-heading text-lg font-bold text-[#1A1422] mb-4">Footer</h3>
        <Field label="Teks footer (pakai {year} untuk otomatis isi tahun)">
          <input className="input-field" value={cfg.footer_text} onChange={(e) => update("footer_text", e.target.value)} />
        </Field>
      </section>

      <div className="sticky bottom-4 z-10 flex gap-3">
        <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center" data-testid="landing-save">
          {saving ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
        <a href="/" target="_blank" rel="noreferrer" className="btn-outline" data-testid="landing-preview">
          Lihat Landing
        </a>
      </div>
    </div>
  );
}


/* ---------------- APP TEXT (universal CMS) ---------------- */
const APP_TEXT_LABELS = {
  navbar: { _label: "Navigasi", home: "Tombol Beranda", courses: "Tombol Kursus", login_button: "Tombol Login (CTA)", dashboard: "Menu Dashboard", catalog: "Menu Katalog", admin: "Menu Admin", tagline: "Tagline kecil di logo" },
  dashboard: { _label: "Dashboard", greeting: "Sapaan (pakai {name} untuk nama)", subtitle: "Subjudul di bawah sapaan", stat_enrolled: "Label stat 'Kursus diikuti'", stat_lessons: "Label stat 'Materi selesai'", stat_progress: "Label stat 'Rata-rata progress'", my_courses_title: "Judul section 'Kursus saya'", empty_text: "Pesan saat belum ada kursus", empty_button: "Tombol di empty state", recommended_title: "Judul section rekomendasi", see_all: "Link 'Lihat semua'", loading: "Teks loading", lessons_suffix: "Suffix jumlah materi" },
  catalog: { _label: "Katalog", page_title: "Judul halaman", page_subtitle: "Subjudul halaman", empty: "Pesan saat tidak ada kursus", enrolled_badge: "Badge 'Terdaftar'", lessons_suffix: "Suffix jumlah materi" },
  course_detail: { _label: "Detail Kursus", back_link: "Link kembali", lessons_suffix: "Suffix jumlah materi", investment_label: "Label di atas harga", voucher_hint: "Hint voucher", voucher_placeholder: "Placeholder input voucher", voucher_apply: "Tombol Pakai voucher", voucher_applied: "Pesan voucher applied (pakai {percent})", continue_learn: "Tombol Lanjut Belajar", enroll_free: "Tombol Daftar Gratis", enroll_paid: "Tombol Beli & Daftar", processing: "Teks tombol saat loading", access_note: "Catatan kecil di bawah tombol" },
  course_learn: { _label: "Halaman Belajar", back_link: "Link kembali", course_label: "Label kecil 'Kursus'", graduated_msg: "Pesan setelah selesai 100%", lesson_prefix: "Prefix nomor materi", section_label: "Label 'Materi' di atas judul", mark_complete: "Tombol tandai selesai", completed_label: "Status 'Sudah selesai'", assignment_title_prefix: "Prefix judul tugas", submission_placeholder: "Placeholder kotak tugas", submission_resend_placeholder: "Placeholder saat kirim ulang", submit_button: "Tombol kirim tugas", resubmit_button: "Tombol kirim ulang", submitted_prefix: "Prefix 'Jawaban kamu (terkirim...'", feedback_label: "Label 'Feedback:'", comments_title: "Judul section diskusi", comment_placeholder: "Placeholder komentar", comments_empty: "Pesan diskusi kosong" },
  login: { _label: "Login", back_link: "Link kembali", title: "Judul utama", subtitle: "Subjudul", google_button: "Teks tombol Google", terms_note: "Catatan T&C kecil" },
  checkout: { _label: "Checkout (pembayaran)", verifying_title: "Judul saat verifikasi", verifying_subtitle: "Subjudul saat verifikasi", success_title: "Judul saat sukses", success_subtitle: "Subjudul saat sukses", expired_title: "Judul saat expired", expired_subtitle: "Subjudul expired", expired_button: "Tombol expired", error_title: "Judul error", error_subtitle: "Subjudul error", error_button: "Tombol error" },
  install: { _label: "Install Aplikasi (PWA)", card_title: "Judul card di dashboard", card_subtitle_mobile: "Subtitle card (di HP/tablet)", card_subtitle_desktop: "Subtitle card (di laptop/desktop)", btn_install_now: "Tombol Install Sekarang", btn_how_to: "Tombol Cara install", guide_title: "Judul modal panduan", guide_android_intro: "Intro panduan Android", guide_ios_intro: "Intro panduan iPhone/iPad", guide_desktop_intro: "Intro panduan Laptop/Desktop", guide_benefit: "Catatan manfaat install (kotak kuning di bawah modal)" },
};

function AppTextTab() {
  const { refresh } = useAppText();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("navbar");

  useEffect(() => {
    api.get("/app-text").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-[#5C5868]">Memuat...</div>;

  const updateField = (section, key, value) => {
    setData({ ...data, [section]: { ...data[section], [key]: value } });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/app-text", data);
      await refresh();
      toast.success("Teks aplikasi tersimpan. Refresh halaman untuk lihat perubahan.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="card-surface p-4 bg-[#FF8C00]/5 border-[#FF8C00]/30">
        <p className="text-sm text-[#5C5868]">
          💡 <strong className="text-[#1A1422]">Tip:</strong> Variabel seperti <code className="bg-[#F4F2F8] px-1 rounded">{`{name}`}</code> akan otomatis diganti dengan data sesungguhnya. Jangan dihapus atau diubah nama variabelnya.
        </p>
      </div>

      {Object.entries(APP_TEXT_LABELS).map(([section, labels]) => {
        const isOpen = openSection === section;
        return (
          <div key={section} className="card-surface overflow-hidden">
            <button
              onClick={() => setOpenSection(isOpen ? null : section)}
              className="w-full flex items-center justify-between p-4 hover:bg-[#F4F2F8]/40 transition-colors"
              data-testid={`apptext-section-${section}`}
            >
              <span className="font-heading font-bold text-[#1A1422]">{labels._label}</span>
              <span className="text-xs text-[#5C5868]">{isOpen ? "▲ tutup" : "▼ buka"}</span>
            </button>
            {isOpen && (
              <div className="p-4 pt-2 border-t border-[#E8E5EE] space-y-3">
                {Object.entries(labels).filter(([k]) => k !== "_label").map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input
                      className="input-field"
                      value={data[section]?.[key] || ""}
                      onChange={(e) => updateField(section, key, e.target.value)}
                      data-testid={`apptext-${section}-${key}`}
                    />
                  </Field>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="sticky bottom-4 z-10 flex gap-3">
        <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center" data-testid="apptext-save">
          {saving ? "Menyimpan..." : "Simpan Semua Teks"}
        </button>
      </div>
    </div>
  );
}


/* ---------------- USERS & MESSAGING ---------------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { user: currentUser } = useAuth();

  const deleteUser = async (e, userId, userName) => {
    e.stopPropagation();
    if (!window.confirm(`Hapus siswa "${userName}"? Semua data enrollment dan tugas akan ikut terhapus.`)) return;
    setDeleting(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Siswa berhasil dihapus");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus siswa");
    } finally {
      setDeleting(null);
    }
  };

  const load = async () => setUsers((await api.get("/admin/users")).data);
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    if (filter === "all") return true;
    if (filter === "inactive") return (u.days_inactive ?? 0) >= 7;
    if (filter === "no_progress") return u.role === "student" && u.submissions_count === 0;
    return true;
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "all", label: "Semua" },
          { id: "inactive", label: "Tidak aktif ≥ 7 hari" },
          { id: "no_progress", label: "Belum kerjakan tugas" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === f.id ? "bg-[#FF8C00] text-white" : "bg-[#F4F2F8] text-[#5C5868] hover:bg-[#E8E5EE]"
            }`}
            data-testid={`users-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && <div className="card-surface p-6 text-[#5C5868] text-sm">Tidak ada pengguna yang cocok.</div>}
        {filtered.map((u) => {
          const inactive = (u.days_inactive ?? 0) >= 7;
          const noProgress = u.role === "student" && u.submissions_count === 0;
          return (
            <button
              key={u.user_id}
              onClick={() => setSelected(u.user_id)}
              className="card-surface card-hover p-4 w-full flex items-center gap-4 text-left"
              data-testid={`user-row-${u.user_id}`}
            >
              {u.picture ? (
                <img src={toAbsUrl(u.picture)} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold flex-shrink-0">
                  {u.name?.[0] || "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-[#1A1422] truncate">{u.name}</div>
                  <RoleBadge role={u.role} />
                  {inactive && u.role === "student" && (
                    <span className="text-[10px] bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Tidak aktif {u.days_inactive}h
                    </span>
                  )}
                  {noProgress && (
                    <span className="text-[10px] bg-[#EF4444]/10 text-[#EF4444] px-2 py-0.5 rounded-full font-bold">Belum kerjakan tugas</span>
                  )}
                </div>
                <div className="text-xs text-[#5C5868] truncate">{u.email}</div>
                <div className="text-xs text-[#8B8794] mt-0.5">
                  {u.role === "mentor" && (u.assigned_students?.length > 0)
                    ? `${u.assigned_students.length} siswa di-assign`
                    : `${u.enrollment_count} kursus • ${u.submissions_count} tugas terkirim`}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {u.role === "student" && (
                  <button
                    onClick={e => { e.stopPropagation(); setPerformance(u.user_id); }}
                    className="p-1.5 rounded-full hover:bg-[#FFF4E6] text-[#FF8C00]"
                    title="Dashboard Performa"
                  >
                    <BarChart2 className="w-4 h-4" />
                  </button>
                )}
                <MessageCircle className="w-5 h-5 text-[#8B8794]" />
                {currentUser?.role === "admin" && u.role !== "admin" && (
                  <button
                    onClick={e => deleteUser(e, u.user_id, u.name)}
                    disabled={deleting === u.user_id}
                    className="p-1.5 rounded-full hover:bg-[#FEF2F2] text-[#EF4444] disabled:opacity-40"
                    title="Hapus Siswa"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <UserDetailModal userId={selected} onClose={() => { setSelected(null); load(); }} />
      )}
      {performance && (
        <StudentPerformance
          userId={performance}
          currentUser={currentUser}
          onClose={() => setPerformance(null)}
        />
      )}
    </div>
  );
}

function UserDetailModal({ userId, onClose }) {
  const { user: me } = useAuth();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const { data } = await api.get(`/admin/users/${userId}`);
    setDetail(data);
  };

  useEffect(() => { load(); }, [userId]);

  if (!detail) {
    return (
      <Modal onClose={onClose} title="Memuat...">
        <div className="text-[#5C5868] text-sm">Memuat data pengguna...</div>
      </Modal>
    );
  }

  const { user, enrollments, messages } = detail;
  const isAdmin = me?.role === "admin";
  const isAdminOrCoach = ["admin", "coach"].includes(me?.role);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface max-w-2xl w-full max-h-[90vh] overflow-y-auto p-0" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E8E5EE] flex items-start gap-4">
          {user.picture ? (
            <img src={toAbsUrl(user.picture)} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold text-xl">
              {user.name?.[0] || "U"}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-xl font-bold text-[#1A1422]">{user.name}</h2>
              <RoleBadge role={user.role} />
            </div>
            <p className="text-sm text-[#5C5868]">{user.email}</p>
            <p className="text-xs text-[#8B8794] mt-1">
              Bergabung {new Date(user.created_at).toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <button onClick={onClose} className="text-[#5C5868] hover:text-[#1A1422]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 px-6 pt-4 border-b border-[#E8E5EE] overflow-x-auto">
          <button onClick={() => setTab("overview")} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === "overview" ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"}`} data-testid="user-tab-overview">
            Overview
          </button>
          <button onClick={() => setTab("message")} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === "message" ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"}`} data-testid="user-tab-message">
            Kirim Pesan
          </button>
          <button onClick={() => setTab("history")} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === "history" ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"}`} data-testid="user-tab-history">
            Riwayat Pesan ({messages.length})
          </button>
          {isAdmin && (
            <button onClick={() => setTab("role")} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === "role" ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"}`} data-testid="user-tab-role">
              Atur Role
            </button>
          )}
          {!isAdmin && isAdminOrCoach && user.role === "mentor" && (
            <button onClick={() => setTab("assign")} className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === "assign" ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"}`} data-testid="user-tab-assign">
              Assign Siswa
            </button>
          )}
        </div>

        <div className="p-6">
          {tab === "overview" && <UserOverview enrollments={enrollments} />}
          {tab === "message" && <SendMessageForm userId={userId} enrollments={enrollments} onSent={load} sender={me} />}
          {tab === "history" && <MessageHistory messages={messages} />}
          {tab === "role" && isAdmin && <RoleAssignForm userId={userId} user={user} onChange={load} isSelf={me?.user_id === userId} />}
          {tab === "assign" && isAdminOrCoach && <MentorAssignForm userId={userId} user={user} />}
        </div>
      </div>
    </div>
  );
}

function MentorAssignForm({ userId, user }) {
  const [assigned, setAssigned] = useState(user.assigned_students || []);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(r => setStudents(r.data.filter(u => u.role === "student")));
  }, []);

  const toggle = (uid) => setAssigned(a => a.includes(uid) ? a.filter(x => x !== uid) : [...a, uid]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}/assignments`, { assigned_students: assigned });
      toast.success("Assignment tersimpan");
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-[#5C5868] font-semibold">Pilih siswa yang di-assign ke {user.name} ({assigned.length} dipilih)</div>
      {students.length === 0 ? (
        <p className="text-sm text-[#8B8794]">Belum ada siswa di sistem.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-1 bg-[#F4F2F8] rounded-xl p-2">
          {students.map((s) => (
            <label key={s.user_id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer">
              <input type="checkbox" checked={assigned.includes(s.user_id)} onChange={() => toggle(s.user_id)} className="w-4 h-4 accent-[#FF8C00]" />
              {s.picture ? (
                <img src={toAbsUrl(s.picture)} className="w-7 h-7 rounded-full object-cover" alt="" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#FF8C00] text-xs font-bold">{s.name?.[0] || "U"}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#1A1422] truncate">{s.name}</div>
                <div className="text-xs text-[#8B8794] truncate">{s.email}</div>
              </div>
            </label>
          ))}
        </div>
      )}
      <button onClick={save} disabled={saving} className="btn-primary mt-2">
        {saving ? "Menyimpan..." : "Simpan Assignment"}
      </button>
    </div>
  );
}

function RoleAssignForm({ userId, user, onChange, isSelf }) {
  const [role, setRole] = useState(user.role);
  const [assigned, setAssigned] = useState(user.assigned_students || []);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role === "mentor") {
      api.get("/admin/users").then(({ data }) => {
        setStudents(data.filter((u) => u.role === "student"));
      });
    }
  }, [role]);

  const saveRole = async () => {
    if (isSelf && role !== "admin") {
      toast.error("Tidak bisa menurunkan role diri sendiri");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      toast.success(`Role diubah menjadi ${role}`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setSaving(false);
    }
  };

  const toggleStudent = (sid) => {
    setAssigned((arr) => arr.includes(sid) ? arr.filter((x) => x !== sid) : [...arr, sid]);
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}/assignments`, { student_ids: assigned });
      toast.success(`${assigned.length} siswa di-assign ke mentor ini`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-xs text-[#5C5868] mb-2 block font-semibold">Role Pengguna</label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {["student", "mentor", "advisor", "coach", "admin"].map((r) => {
            const meta = ROLE_META[r];
            const I = meta.Icon;
            const active = role === r;
            return (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`p-3 rounded-xl text-left border-2 transition-all ${active ? "border-[#FF8C00] bg-[#FFF8F0]" : "border-[#E8E5EE] hover:border-[#FF8C00]/50"}`}
                data-testid={`role-option-${r}`}
              >
                <I className={`w-4 h-4 mb-1 ${active ? "text-[#FF8C00]" : "text-[#8B8794]"}`} />
                <div className={`text-sm font-bold ${active ? "text-[#FF8C00]" : "text-[#1A1422]"}`}>{meta.label}</div>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[#8B8794] mt-2 space-y-0.5">
          <div><strong>Siswa:</strong> hanya akses kursus & belajar.</div>
          <div><strong>Mentor:</strong> akses ke siswa yg di-assign + tugas mereka.</div>
          <div><strong>Advisor:</strong> lihat analitik + pengguna + tugas siswa. Tidak bisa edit kursus/materi.</div>
          <div><strong>Coach:</strong> kelola kursus + materi + semua siswa + tugas.</div>
          <div><strong>Admin:</strong> akses penuh termasuk voucher & konten landing.</div>
        </div>
        {role !== user.role && (
          <button onClick={saveRole} disabled={saving} className="btn-primary mt-3" data-testid="save-role-button">
            {saving ? "Menyimpan..." : `Ubah ke ${ROLE_META[role].label}`}
          </button>
        )}
      </div>

      {role === "mentor" && user.role === "mentor" && (
        <div className="border-t border-[#E8E5EE] pt-5">
          <label className="text-xs text-[#5C5868] mb-2 block font-semibold">Pilih siswa yang di-assign ({assigned.length} dipilih)</label>
          {students.length === 0 ? (
            <p className="text-sm text-[#8B8794]">Belum ada siswa di sistem.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1 bg-[#F4F2F8] rounded-xl p-2">
              {students.map((s) => (
                <label key={s.user_id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer" data-testid={`assign-student-${s.user_id}`}>
                  <input
                    type="checkbox"
                    checked={assigned.includes(s.user_id)}
                    onChange={() => toggleStudent(s.user_id)}
                    className="w-4 h-4 accent-[#FF8C00]"
                  />
                  {s.picture ? (
                    <img src={toAbsUrl(s.picture)} className="w-7 h-7 rounded-full object-cover" alt="" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#FF8C00] text-xs font-bold">{s.name?.[0] || "U"}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1A1422] truncate">{s.name}</div>
                    <div className="text-xs text-[#8B8794] truncate">{s.email}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <button onClick={saveAssignments} disabled={saving} className="btn-primary mt-3" data-testid="save-assignments-button">
            {saving ? "Menyimpan..." : "Simpan Assignment"}
          </button>
        </div>
      )}
    </div>
  );
}

function UserOverview({ enrollments }) {
  if (enrollments.length === 0) {
    return (
      <div className="text-center text-[#5C5868] py-8">
        <p>Pengguna ini belum mendaftar kursus apa pun.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {enrollments.map((e, i) => {
        const stuck = e.progress_percent < 30 && e.submissions.length === 0;
        return (
          <div key={i} className="bg-[#F4F2F8] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <div className="font-semibold text-[#1A1422]">{e.course.title}</div>
                <div className="text-xs text-[#5C5868] mt-0.5">
                  Daftar {new Date(e.enrolled_at).toLocaleDateString("id-ID")}
                </div>
              </div>
              {stuck && <span className="text-[10px] bg-[#EF4444]/10 text-[#EF4444] px-2 py-0.5 rounded-full font-bold flex-shrink-0">Stuck</span>}
            </div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden">
                <div className="h-full bg-[#FF8C00]" style={{ width: `${e.progress_percent}%` }} />
              </div>
              <span className="text-xs text-[#5C5868] font-semibold">{e.progress_percent}%</span>
            </div>
            <div className="text-xs text-[#5C5868]">
              {e.completed_lessons.length}/{e.lesson_count} materi selesai • {e.submissions.length} tugas terkirim
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SendMessageForm({ userId, enrollments, onSent, sender }) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [courseId, setCourseId] = useState("");
  const [sending, setSending] = useState(false);

  const templates = [
    { label: "Pengingat belajar", text: "Halo! Sudah lama saya tidak melihat kamu di platform. Yuk lanjutkan materinya, jangan sampai semangat awalnya hilang ya 🚀" },
    { label: "Pengingat tugas", text: "Halo! Kamu punya tugas yang belum dikerjakan. Kalau ada kendala, balas pesan ini ya, saya bantu." },
    { label: "Apresiasi", text: "Selamat! Progress kamu bagus banget. Pertahankan ya, sebentar lagi selesai 💪" },
  ];

  const send = async () => {
    if (!content.trim()) return toast.error("Pesan tidak boleh kosong");
    setSending(true);
    try {
      await api.post(`/admin/users/${userId}/message`, {
        subject: subject || undefined,
        content,
        course_id: courseId || undefined,
      });
      toast.success("Pesan terkirim!");
      setSubject("");
      setContent("");
      setCourseId("");
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {templates.map((t) => (
          <button key={t.label} onClick={() => setContent(t.text)} className="text-xs bg-[#F4F2F8] hover:bg-[#E8E5EE] px-3 py-1.5 rounded-full" data-testid={`template-${t.label}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Field label="Subjek (opsional)">
        <input className="input-field" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Pesan dari ${sender?.role || "mentor"}`} data-testid="message-subject" />
      </Field>

      {enrollments.length > 0 && (
        <Field label="Terkait kursus (opsional)">
          <select className="input-field" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">— umum, bukan untuk kursus spesifik —</option>
            {enrollments.map((e) => (
              <option key={e.course.course_id} value={e.course.course_id}>{e.course.title}</option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Isi pesan">
        <textarea
          rows={6}
          className="input-field"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Tulis pesan untuk siswa..."
          data-testid="message-content"
        />
      </Field>

      <button onClick={send} disabled={sending} className="btn-primary w-full justify-center" data-testid="message-send">
        <Send className="w-4 h-4" /> {sending ? "Mengirim..." : "Kirim Pesan"}
      </button>
    </div>
  );
}

function MessageHistory({ messages }) {
  if (messages.length === 0) return <div className="text-center text-[#5C5868] py-8">Belum ada pesan terkirim ke pengguna ini.</div>;
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <div key={m.message_id} className="bg-[#F4F2F8] rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-[#1A1422]">{m.subject}</span>
            <span className="text-[10px] text-[#8B8794]">{new Date(m.created_at).toLocaleString("id-ID")}</span>
          </div>
          <p className="text-sm text-[#5C5868] whitespace-pre-line">{m.content}</p>
          <div className="mt-1.5 text-[10px]">
            {m.read ? (
              <span className="text-[#10B981]">✓ Dibaca {m.read_at && new Date(m.read_at).toLocaleString("id-ID")}</span>
            ) : (
              <span className="text-[#8B8794]">Belum dibaca</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
